// src/components/common/layout/Navbar.js
// Updated: 10/20/2025 07:46 AM (Added Hamburger Menu)

import React, { useState } from 'react'; // Added useState
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useFamily } from '../../../context/FamilyContext';
import { auth } from '../../../firebase/config';
import { signOut } from 'firebase/auth';
import toast from 'react-hot-toast';
// Added FaBars and FaTimes icons for the hamburger menu
import { FaSignOutAlt, FaUserCircle, FaHome, FaUsers, FaTree, FaAddressBook, FaImages, FaBars, FaTimes } from 'react-icons/fa';

const Navbar = () => {
  const { userData } = useAuth();
  const { familyData, isHost } = useFamily();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // State for mobile menu

  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast.success('Signed out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Failed to sign out');
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  }

  // Define nav links data to avoid repetition
  const navLinks = [
    { to: "/", icon: FaHome, text: "Home" },
    ...(isHost ? [{ to: "/dashboard", icon: FaUsers, text: "Dashboard" }] : []),
    { to: "/directory", icon: FaAddressBook, text: "Directory" },
    { to: "/gallery", icon: FaImages, text: "Gallery" },
    { to: "/family-tree", icon: FaTree, text: "Family Tree" },
  ];

  return (
    <nav className="bg-white shadow-md relative"> {/* Added relative positioning */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left side: Hamburger Menu Button (visible on small screens) */}
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-neutral-gray hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
              aria-controls="mobile-menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              {isMobileMenuOpen ? (
                <FaTimes className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <FaBars className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>

          {/* Center: Brand/Family Name (adjust flex order if needed) */}
          <div className="flex-shrink-0 flex items-center">
             {/* Link moved inside to align better */}
            <Link to="/" className="text-2xl font-bold text-primary">
              {familyData?.familyName || 'Family Reunion'}
            </Link>
          </div>

          {/* Center: Desktop Navigation Links (hidden on small screens) */}
          <div className="hidden md:block md:ml-6">
            <div className="flex items-baseline space-x-4">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-gray-600 hover:bg-gray-100 hover:text-neutral-gray px-3 py-2 rounded-md text-sm font-medium flex items-center gap-2"
                >
                  <link.icon /> {link.text}
                </Link>
              ))}
            </div>
          </div>

          {/* Right side: User Info & Logout (always visible) */}
          <div className="flex items-center ml-4 md:ml-6">
            <span className="text-gray-700 text-sm mr-3 hidden sm:block">
              Welcome, {userData?.firstName || 'User'}
            </span>
            <Link to="/profile" className="p-1 rounded-full text-gray-400 hover:text-neutral-gray focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary" title="Profile">
              <span className="sr-only">View profile</span>
              <FaUserCircle className="h-6 w-6" />
            </Link>
            <button
              onClick={handleLogout}
              className="ml-3 p-1 rounded-full text-gray-400 hover:text-neutral-gray focus:outline-none"
              title="Sign Out"
            >
              <span className="sr-only">Sign out</span>
              <FaSignOutAlt className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu (conditionally rendered) */}
      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-16 left-0 w-full bg-white shadow-lg z-10" id="mobile-menu">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={closeMobileMenu} // Close menu on link click
                className="text-gray-600 hover:bg-gray-100 hover:text-neutral-gray block px-3 py-2 rounded-md text-base font-medium flex items-center gap-2"
              >
                <link.icon /> {link.text}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;